/**
 * The document of a workflow in the workspace collection. Unlike a page it
 * has no entry in the page list: it is registered when it is first needed.
 */
import { buildLocalWorkflowDocId } from '@streamlain/workflow-field-schemas';
import {
  readWorkflowDoc,
  type WorkflowDocDefinition,
} from '@streamlain/workflow-field-schemas/workflow-doc-format';
import type { DocImpl } from '@streamlain/workspace-editor/workspace/doc';

import type { Workspace } from '../../../workspace';
import type { WorkspaceImpl } from '../../../workspace/impls/workspace';

/** The workflow document of the collection, registered on first use. */
export function registerWorkflowDoc(
  workspace: Workspace,
  workflowId: string
): { guid: string; doc: DocImpl } {
  const guid = buildLocalWorkflowDocId(workflowId);
  const collection = workspace.docCollection as unknown as WorkspaceImpl;
  collection.registerDoc(guid);
  const doc = collection.getDoc(guid) as unknown as DocImpl | null;
  if (!doc) {
    throw new Error(`The workflow document ${guid} could not be registered`);
  }
  return { guid, doc };
}

/**
 * The definition the workflow document holds once the doc engine loaded it,
 * for readers outside the builder.
 */
export async function readWorkflowDocDefinition(
  workspace: Workspace,
  workflowId: string,
  signal?: AbortSignal
): Promise<WorkflowDocDefinition> {
  const { guid, doc } = registerWorkflowDoc(workspace, workflowId);
  doc.getStore({ id: guid }).load();
  await workspace.engine.doc.waitForDocLoaded(guid, signal);
  return readWorkflowDoc(doc.spaceDoc);
}
